import {useTranslation} from 'react-i18next';
import React, {Fragment, useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import {FormControl, FormControlLabel, Radio, RadioGroup} from '@mui/material';
import {consultantDictionary, ControlConsultantComponent} from '../../../controls/agyle/clinitian';
import _ from 'lodash';
import moment from 'moment';
import MessageBox from '../../../util/messageBox';
import FormLabel from '@mui/material/FormLabel';
import {useDispatch, useSelector} from 'react-redux';
import {hasPermission, Permissions, user} from '../../../store/reducers/user';
import {calculateArrivalDate} from '../../../util/patientUtil';
import API from '../../../app/api';
import keyPerformanceIndicator from '../../../util/timeMarker';
import Notes from '../../../util/notes';
import Formatter from '../../../util/formatter';
import {setSnackbar} from '../../../store/reducers/globals';
import {patientBanner} from '../../../controls/agyle/tasks/components/patientBanner';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import InformationBox from '../../../components/other/informationBox';
import {addNoteForPatient} from '../../../store/reducers/notes';
import useOpenState from '../../../hooks/useOpenState';
import {addWorkflowStep, nextWorkflowStep} from '../../../store/reducers/workflow';
import AgyleDictionaryField from '../../../components/ui/selector/agyleDictionaryField';
import {transferHospitalsDict} from '../../../store/reducers/dicts';
import {addOrReplace, getPatientFlag, getPatientFlagValue} from '../mainScreen/util/patientFlagsUtil';
import MiniFormDialog from '../../../components/modal/miniFormDialog';
import {CLINICAL_CONCLUSION_INNER_STATE} from './formsUtil';
import {getConfig} from '../../../util/configUtil';
import {WORKFLOW_OPTIONS} from '../../../util/workflowUtil';

const ClinicalConclusion = (props) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const controller = new AbortController();

    // Dict
    const hospitalsList = useSelector(transferHospitalsDict);
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    // Configs
    const deceasedDateEnabled = getConfig('modules.patient.dodDateUpdateEnabled');
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const verificationOfDeathAgyleMiniForm = getConfig('verificationOfDeathAgyleMiniForm');
    const clinicalConclusionNoteEnabled = getConfig('clinicalConclusionNoteEnabled');

    // Deceased Date
    const [deceasedDate, setDeceasedDate] = useState(null);

    // Data
    const [form, setForm] = useState(CLINICAL_CONCLUSION_INNER_STATE);
    const [formIsModified, setFormIsModified] = React.useState(props.isActionMode);

    // Data picker validation
    const [errorPicker, setErrorPicker] = useState(null);

    // Message Box opener state
    const [isClearConfirmOpen, openClearConfirm, closeClearConfirm] = useOpenState(false, true);

    // Init
    useEffect(() => {
        let data = {
            who: null,
            when: moment().toISOString(),
            status: null,
            transfer: null,
        };

        if (props.initStatus) {
            let newFilter = 'deactivated ne true';
            API.getDictByURL(`/$dict/ClinicalConclusionClinicians?$filter=${encodeURIComponent(newFilter)}`, controller.signal).then(dictData => {
                if (dictData) {
                    for (let clinitian of dictData.__data) {
                        if (clinitian.userName === userObject.userName) {
                            setForm(prevState => ({
                                ...prevState,
                                who: clinitian,
                                status: props.initStatus,
                                when: moment().toISOString(),
                            }));
                            break;
                        } else {
                            setForm(prevState => ({
                                ...prevState,
                                who: null,
                                status: props.initStatus,
                                when: moment().toISOString(),
                            }));
                            break;
                        }
                    }
                }
            });
        } else {
            if (props?.patient?.clinicalConclusion) {
                data = {
                    ...props.patient.clinicalConclusion,
                    when: props.patient.clinicalConclusion.when
                        ? moment(props.patient.clinicalConclusion.when, defaultDateTimeFormat).isValid()
                            ? moment(props.patient.clinicalConclusion.when, defaultDateTimeFormat) : moment(props.patient.clinicalConclusion.when)
                        : moment().toISOString(),
                    status: props.patient.clinicalConclusion.status ? props.patient.clinicalConclusion.status : null,
                    transfer: _.isEmpty(props.patient.clinicalConclusion?.transfer) ? null : props.patient.clinicalConclusion?.transfer,
                };
                if (_.isString(data?.transfer)) {
                    const hospital = _.find(hospitalsList, {__code: data?.transfer});
                    data = {
                        ...data,
                        transfer: hospital || null,
                    };
                }
            }

            if (props.patient?.metadata?.patientFlags) {
                const deceased = getPatientFlag(props.patient, 'deceased');
                data = {
                    ...data,
                    status: deceased?.checked ? 'Deceased' : data.status === 'Deceased' ? '' : data.status,
                };

                if (deceased?.checked && deceasedDateEnabled) {
                    setDeceasedDate(deceased?.deceasedDate || null);
                }
            }

            if (_.isUndefined(data?.who) || _.isEmpty(data?.who)) {
                let newFilter = 'deactivated ne true';
                API.getDictByURL(`/$dict/ClinicalConclusionClinicians?$filter=${encodeURIComponent(newFilter)}`, controller.signal).then(dictData => {
                    if (dictData) {
                        for (let clinitian of dictData.__data) {
                            if (clinitian.userName === userObject.userName) {
                                setForm(prevState => ({
                                    ...prevState,
                                    who: clinitian,
                                }));
                                break;
                            }
                        }
                    }
                });
            }
        }

        setForm(data);
        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (props.agyleDialogInsertButtons) {
            const buttons = [];
            if (!props.disableSave) {
                buttons.push(
                    <AgyleButton onClick={openClearConfirm}>
                        {t('Delete')}
                    </AgyleButton>,
                    <AgyleButton variant="contained" disabled={!formIsModified} onClick={submit}>
                        {t('Submit')}
                    </AgyleButton>,
                );
            }
            props.agyleDialogInsertButtons(buttons);
        }
    }, [props.disableSave, formIsModified, form, errorPicker, userObject, deceasedDate]);

    const findHospitalName = (code) => {
        if (code) {
            const hospital = _.find(hospitalsList, {__code: code});
            return hospital.__description || '';
        }
        return '';
    };

    const prepareNote = (customData) => {
        const hospitalName = _.isString(form.transfer) ? findHospitalName(form.transfer) : form?.transfer?.__description;
        return Notes.getClinicalConclusionNote(customData || form, hospitalName || 'hospitalName', deceasedDate);
    };

    const validation = () => {
        if (form.who && form.when && !errorPicker) {
            if (form.status === 'Transfer') {
                const isHospital = form.transfer !== '';
                if (!isHospital) dispatch(setSnackbar({type: 'warning', message: t('Please select a hospital.')}));
                return isHospital;
            } else if (form.status === 'Deceased' && deceasedDateEnabled) {
                const isDateValid = deceasedDate && moment(deceasedDate).isValid();
                if (!isDateValid) dispatch(setSnackbar({
                    type: 'warning',
                    message: t('Please select a deceased date.'),
                }));
                return isDateValid;
            }
            return true;
        } else if (!form.who && !form.when && !form.status) {
            return true;
        }
        dispatch(setSnackbar({type: 'warning', message: t('Not all fields have been filled in.')}));
        return false;
    };

    const submit = () => {
        if (validation() && !errorPicker) {
            let data = _.cloneDeep(form);
            const dateOfForm = moment().toISOString();

            data = {
                ...data,
                by: userFullName,
                login: userObject?.userName,
                transfer: data.status === 'Transfer' ? data.transfer : null,
                when: data.when ? moment(data.when).toISOString() : null,
                timestamp: dateOfForm,
            };

            let objToSave = ([
                {
                    name: 'clinicalConclusion',
                    value: data,
                    noteToAdd: prepareNote(),
                },
            ]);

            if (data.status === 'Deceased' || getPatientFlagValue(props.patient, 'deceased')) {
                let clone = [];
                if (props.patient?.metadata?.patientFlags) {
                    clone = _.cloneDeep(props.patient.metadata.patientFlags);
                }

                let deceasedFlag = {
                    name: 'deceased',
                    checked: data.status === 'Deceased',
                    by: userFullName,
                    login: userObject?.userName,
                    date: moment().toISOString(),
                };

                if (deceasedDateEnabled) {
                    deceasedFlag = {
                        ...deceasedFlag,
                        deceasedDate: moment(deceasedDate).toISOString(),
                    };
                }

                addOrReplace(clone, deceasedFlag);
                objToSave.push({
                    name: 'metadata.patientFlags',
                    value: clone,
                });

                if (verificationOfDeathAgyleMiniForm && dispatch(hasPermission(Permissions.AGYLE_CAN_VERIFICATIONOFDEATH))) {
                    dispatch(addWorkflowStep([WORKFLOW_OPTIONS.VERIFICATION_OF_DEATH]));
                    dispatch(nextWorkflowStep());
                }
            }

            if (!_.isNull(data.status) && data.status !== 'Deceased') {
                keyPerformanceIndicator(
                    objToSave,
                    props.patient,
                    ['firstClinicallyReadyToProceedSaved'],
                    {
                        when: data.when,
                        who: data.who,
                    },
                );
            }

            if (data.status === 'Admitted' && !_.isEmpty(props.patient?.bedRequests)) {
                let clone = _.cloneDeep(props.patient?.bedRequests);
                let findBR = _.find(clone, br => !br?.followUp && br?.status === 'NEW');
                if (!findBR?.patientReady?.checked) {
                    if (!findBR.patientReady) {
                        findBR.patientReady = {};
                    }
                    findBR.patientReady.checked = true;
                    findBR.patientReady.user = userFullName;
                    findBR.patientReady.login = userObject?.userName;
                    findBR.patientReady.timestamp = moment().toISOString();

                    // clear object
                    delete findBR.__href;
                    delete findBR.__type;
                    delete findBR.__version;
                    delete findBR._createdBy;
                    delete findBR._createdDate;
                    API.saveBedRequest(findBR)
                        .then((res) => {
                            dispatch(addNoteForPatient(props.patient?.patient.__id, {
                                note: Notes.getBedRequestExternalNote(res),
                                noteType: 'bedRequest',
                                patientsInWardsRef: [props.patient.__id],
                                episodeId: props?.patient?.admission?.consultantEpisodeId || null,
                            }, props.patient.__id));
                            if (props.modifyPatient) {
                                props.modifyPatient([{
                                    name: 'bedRequests',
                                    value: clone,
                                }]);
                            }
                        })
                        .catch(() => {
                            dispatch(setSnackbar({
                                type: 'error',
                                message: t('There was a problem with saving bed request.'),
                            }));
                        });
                }
            }

            if (props.onSave) props.onSave(objToSave);
            if (props.onChange) props.onChange(objToSave);
            props.onClose();
        }
    };

    const clearData = () => {
        closeClearConfirm();
        let objToSave = ([
            {
                name: 'clinicalConclusion',
                value: null,
                noteToAdd: prepareNote(CLINICAL_CONCLUSION_INNER_STATE),
            },
        ]);

        if (!props.patient?.admission?.endDate && !props.patient?.studies?.discharge) {
            objToSave.push({
                name: 'metadata.firstClinicallyReadyToProceedSaved',
                value: null,
            });
        }

        if (props.onSave) props.onSave(objToSave);
        if (props.onChange) props.onChange(objToSave);
        props.onClose();
    };

    return (
        <>
            <MiniFormDialog
                title={t(props._formTitle)}
                open={props.open}
                onClose={props.onClose}
                onSave={submit}
                disableSubmit={!formIsModified && props.disableSave}
                formOptions={props._formOptions}
                buttons={[
                    <AgyleButton
                        onClick={openClearConfirm}
                        key="delete"
                    >
                        {t('Delete')}
                    </AgyleButton>,
                ]}
            >
                <Grid container spacing={2} style={{padding: '10px 0'}}>
                    {(props.isActionMode || props.patientInfo) &&
                        <Grid item xs={12}>
                            {patientBanner(props.patient, true)}
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <ControlConsultantComponent
                            typeDict={'ClinicalConclusionClinicians'}
                            agyleRefresh
                            error={!form.who && (!!form.when || !!form.status)}
                            label={'Clinical Conclusion Clinician'}
                            allowedConsultants={consultantDictionary.clinicalConclusion}
                            value={form.who}
                            onChange={event => {
                                setForm(prevState => ({...prevState, who: event}));
                                setFormIsModified(true);
                            }}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDatePicker
                            type="dateTime"
                            error={!form.when && (form.who || form.status)}
                            label={t('Clinical Conclusion Date and Time')}
                            value={form.when}
                            minDate={props.isBB ? null : moment(calculateArrivalDate(props.patient))}
                            maxDate={moment()}
                            onError={setErrorPicker}
                            onChange={event => {
                                setForm(prevState => ({...prevState, when: event}));
                                setFormIsModified(true);
                            }}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">
                                {t('Status')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={form.status}
                                onChange={event => {
                                    setForm(prevState => ({
                                        ...prevState,
                                        status: event.target.value,
                                    }));
                                    setFormIsModified(true);
                                }}
                            >
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        value="Deceased"
                                        control={<Radio/>}
                                        label={t('Deceased')}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        value="Admitted"
                                        control={<Radio/>}
                                        label={t('Ready to Admit to Ward')}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        value="Transfer"
                                        control={<Radio/>}
                                        label={t('Ready to Transfer')}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        value="Discharge"
                                        control={<Radio/>}
                                        label={t('Ready to Discharge')}
                                    />
                                </Grid>
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {form.status === 'Transfer' &&
                        <Grid item xs={12}>
                            <AgyleDictionaryField
                                label={t('Hospital')}
                                options={hospitalsList}
                                value={form.transfer || null}
                                onChange={(_e, val) => {
                                    setForm(prevState => ({...prevState, transfer: val}));
                                    setFormIsModified(true);
                                }}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    }

                    {(deceasedDateEnabled && form.status === 'Deceased') &&
                        <Grid item xs={12}>
                            <AgyleDatePicker
                                type="dateTime"
                                label={t('Deceased Date and Time')}
                                error={!deceasedDate}
                                value={deceasedDate}
                                onError={setErrorPicker}
                                onChange={event => {
                                    setDeceasedDate(event);
                                    setFormIsModified(true);
                                }}
                                maxDate={moment()}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    }

                    {clinicalConclusionNoteEnabled &&
                        <Grid item xs={12}>
                            <AgyleTextField
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                variant="outlined"
                                className="custom-dt"
                                placeholder={t('Enter text')}
                                style={{width: '100%'}}
                                label={t('Note')}
                                value={form.note}
                                multiline
                                onChange={event => {
                                    setForm(prevState => ({...prevState, note: event.target.value}));
                                    setFormIsModified(true);
                                }}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    }

                    {((verificationOfDeathAgyleMiniForm && form.status === 'Deceased') && !dispatch(hasPermission(Permissions.AGYLE_CAN_VERIFICATIONOFDEATH))) &&
                        <Grid item xs={12}>
                            <InformationBox type="warning">
                                {t('Verification of death form must be completed before discharge')}
                            </InformationBox>
                        </Grid>
                    }
                </Grid>
            </MiniFormDialog>

            <MessageBox
                title={t('CAUTION')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={clearData}
                onClose={closeClearConfirm}
                message={t('Are you sure you want delete all recorded values? This will also remove \'Clinically Ready to Proceed Date and Time\' timestamp set.')}
                open={isClearConfirmOpen}
            />
        </>
    );
};

export default ClinicalConclusion;

